import React, { useEffect, useState } from "react";
import { Container, Row, Col, Form } from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { post, put, get } from "../../../api/axios";
import endpoints from "../../../api/endpoints";
import CustomInput from "../../../components/AuthenticationComponents/CustomInput";
import CustomSelector from "../../../components/AuthenticationComponents/CustomSelector";
import CustomMultiSelector from "../../../components/AuthenticationComponents/CustomMultiSelector";
import SecondaryButton from "../../../components/Common/SecondaryButton";
import tickIcon from "../../../assets/images/support/tick-circle-white.svg";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import toastr from "toastr";
import "toastr/build/toastr.min.css";
import { extractErrorMessage } from "../../../utils/errorHandler";
import cancelIcon from "../../../assets/images/user-dashboard/remove-circle.svg";

/* -------------------------------------------------------------------------
 * AdminGameCreatePage — create/edit form for a single game.
 * ---------------------------------------------------------------------- */

const getGameSaveErrorMessage = (error, fallback = "Failed to save game. Please try again.") => {
  const responseData = error.response?.data;
  const extractedMessage = extractErrorMessage(error);

  return (typeof responseData?.message === "string" && responseData.message.trim())
    || (typeof responseData?.error === "string" && responseData.error.trim())
    || (typeof extractedMessage === "string" && extractedMessage.trim() && extractedMessage !== "[object Object]" && extractedMessage)
    || fallback;
};

const AdminGameCreatePage = ({ mode = "create" }) => {
  document.title = mode === "create" ? "Create Game | Omaha Food Lovers" : "Edit Game | Omaha Food Lovers";
  const navigate = useNavigate();
  const location = useLocation();
  const { gameSlug } = useParams();
  const [loading, setLoading] = useState(false);
  const [restaurants, setRestaurants] = useState([]);
  const [loadingRestaurants, setLoadingRestaurants] = useState(false);
  const [hasMoreRestaurants, setHasMoreRestaurants] = useState(true);
  const [restaurantPage, setRestaurantPage] = useState(1);
  const [selectedRestaurants, setSelectedRestaurants] = useState([]);
  const [totalRestaurantCount, setTotalRestaurantCount] = useState(0);

  toastr.options = {
    positionClass: "toast-bottom-left",
    timeOut: 5000,
    closeButton: true,
    progressBar: true,
  };

  // Fetch restaurants for multi-select with pagination
  const fetchRestaurants = async (pageNum = 1, isLoadMore = false) => {
    if (isLoadMore) {
      setLoadingRestaurants(true);
    } else {
      setLoadingRestaurants(true);
    }

    try {
      const response = await get(`${endpoints.allListing}?page=${pageNum}&per_page=20`);
      if (response.data?.data) {
        const data = response.data.data;
        const rawRestaurants = data.data || [];

        const restaurantOptions = rawRestaurants.map((restaurant) => ({
          value: restaurant.id,
          label: restaurant.title || `Restaurant ${restaurant.id}`,
        }));

        if (isLoadMore) {
          setRestaurants((prev) => [...prev, ...restaurantOptions]);
        } else {
          setRestaurants(restaurantOptions);
          // Set total restaurant count on initial load
          setTotalRestaurantCount(data.total || 0);
        }

        setHasMoreRestaurants(data.current_page < data.last_page);
        setRestaurantPage(pageNum);
      }
    } catch (error) {
      console.error("Failed to fetch restaurants:", error);
    } finally {
      setLoadingRestaurants(false);
    }
  };

  useEffect(() => {
    fetchRestaurants(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLoadMoreRestaurants = () => {
    if (!loadingRestaurants && hasMoreRestaurants) {
      fetchRestaurants(restaurantPage + 1, true);
    }
  };

  // Populate form from navigation state for edit mode
  useEffect(() => {
    if (mode === "edit" && gameSlug) {
      const stateGameData = location.state?.gameData;
      if (stateGameData) {
        const config = stateGameData.config || {};
        const restaurantIds = config.eligible_restaurants?.restaurant_ids || [];
        setSelectedRestaurants(restaurantIds);

        formik.setValues({
          name: stateGameData.name || "",
          slug: stateGameData.slug || "",
          type: stateGameData.type || "streak",
          description: stateGameData.description || "",
          reward_points: config.reward_points || 0,
          target_type: config.target_type || "streak",
          target_duration_weeks: config.target_duration_weeks || 4,
          target_visits_required: config.target_visits_required || (config.eligible_restaurants?.scope === "all" ? totalRestaurantCount : restaurantIds.length),
          target_timeframe_weeks: config.target_timeframe_weeks || 6,
          restaurant_scope: config.eligible_restaurants?.scope || "all",
          is_active: stateGameData.is_active !== undefined ? stateGameData.is_active : true,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, gameSlug]);

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .required("Name is required")
      .min(2, "Name must be at least 2 characters")
      .max(100, "Name must not exceed 100 characters"),
    slug: Yup.string()
      .required("Slug is required")
      .min(2, "Slug must be at least 2 characters")
      .max(100, "Slug must not exceed 100 characters")
      .matches(/^[a-z0-9-]+$/, "Slug must only contain lowercase letters, numbers, and hyphens"),
    type: Yup.string().required("Type is required").oneOf(["streak", "visits"], "Type must be either streak or visits"),
    description: Yup.string()
      .required("Description is required")
      .min(10, "Description must be at least 10 characters")
      .max(500, "Description must not exceed 500 characters"),
    reward_points: Yup.number()
      .required("Reward points is required")
      .min(0, "Reward points must be at least 0")
      .max(100000, "Reward points must not exceed 100000"),
    target_type: Yup.string().required("Target type is required"),
    target_duration_weeks: Yup.number()
      .transform((value, originalValue) => originalValue === "" ? null : value)
      .nullable()
      .when("type", {
        is: "streak",
        then: (schema) => schema
          .required("Target duration weeks is required for streak games")
          .oneOf([1, 2, 4, 8], "Select a valid streak duration"),
        otherwise: (schema) => schema.notRequired(),
      }),
    target_visits_required: Yup.number()
      .transform((value, originalValue) => originalValue === "" ? null : value)
      .nullable()
      .when("type", {
        is: "visits",
        then: (schema) => schema
          .required("Target visits required is required for visit games")
          .min(1, "Target visits required must be at least 1"),
        otherwise: (schema) => schema.notRequired(),
      }),
    target_timeframe_weeks: Yup.number()
      .transform((value, originalValue) => originalValue === "" ? null : value)
      .nullable()
      .notRequired(),
    restaurant_scope: Yup.string()
      .required("Restaurant scope is required")
      .oneOf(["all", "selected"], "Restaurant scope must be either all or selected"),
  });

  const formik = useFormik({
    initialValues: {
      name: "",
      slug: "",
      type: "streak",
      description: "",
      reward_points: 0,
      target_type: "streak",
      target_duration_weeks: 4,
      target_visits_required: 5,
      target_timeframe_weeks: "",
      restaurant_scope: "all",
      is_active: true,
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setTouched, validateForm }) => {
      setTouched({
        name: true,
        slug: true,
        type: true,
        description: true,
        reward_points: true,
        target_type: true,
        target_duration_weeks: true,
        target_visits_required: true,
        target_timeframe_weeks: true,
        restaurant_scope: true,
        is_active: true,
      });

      const errors = await validateForm();
      if (Object.keys(errors).length > 0) {
        const firstError = Object.values(errors).find(
          (message) => typeof message === "string" && message.trim()
        );

        toastr.error(firstError || "Please correct the highlighted fields.");
        return;
      }

      setLoading(true);

      try {
        const config = {
          reward_points: parseInt(values.reward_points),
          target_type: values.target_type,
          eligible_restaurants: {
            scope: values.restaurant_scope,
            restaurant_ids: values.restaurant_scope === "selected" ? selectedRestaurants : [],
          },
        };

        if (values.type === "streak") {
          config.target_duration_weeks = parseInt(values.target_duration_weeks);
        } else if (values.type === "visits") {
          config.target_visits_required = parseInt(values.target_visits_required);
          if (values.target_timeframe_weeks !== "" && values.target_timeframe_weeks !== null && values.target_timeframe_weeks !== undefined) {
            config.target_timeframe_weeks = parseInt(values.target_timeframe_weeks);
          }
        }

        const payload = {
          name: values.name,
          slug: values.slug,
          type: values.type,
          description: values.description,
          config,
          is_active: values.is_active,
        };

        let response;
        if (mode === "create") {
          response = await post(endpoints.adminGames, payload);
        } else if (mode === "edit" && gameSlug) {
          const updateEndpoint = endpoints.adminGame.replace('{{game_slug}}', gameSlug);
          response = await put(updateEndpoint, payload);
        }


        // Check for success response (handle different response structures)
        const isSuccess = response?.data?.success || response?.data?.status || response?.status === 200 || response?.status === 201;
        
        if (isSuccess) {
          toastr.success(mode === "create" ? "Game created successfully" : "Game updated successfully");
          // Navigate to games listing page after successful update
          navigate("/admin/games", { replace: true });
        } else {
          console.error("Game save failed. Response:", response);
          toastr.error("Failed to save game. Please try again.");
        }
      } catch (err) {
        console.error("Failed to save game:", err);
        console.error("Error response:", err.response?.data);

        const responseData = err.response?.data;

        if (err.response?.status === 409 && responseData?.error_code === "game_has_active_user_progress") {
          toastr.error(
            getGameSaveErrorMessage(err, "This game cannot be updated because one or more users currently have active progress in it."),
            "Cannot Update Game"
          );
        } else if (err.response?.status === 422) {
          // Validation error - extract and display specific error messages
          const errorData = responseData;
          let errorMessage = "Validation error: ";
          
          if (errorData?.errors) {
            const validationErrors = Object.values(errorData.errors)
              .filter((value) => Array.isArray(value))
              .flat();
            errorMessage += validationErrors.join(", ");
          } else if (errorData?.message) {
            errorMessage += errorData.message;
          } else if (typeof errorData === 'string') {
            errorMessage += errorData;
          } else {
            errorMessage += "Please check your input and try again.";
          }
          
          toastr.error(errorMessage);
        } else if (err.response?.data?.errors) {
          const validationErrors = Object.values(err.response.data.errors)
            .filter((value) => Array.isArray(value))
            .flat();
          toastr.error(validationErrors.length > 0 ? validationErrors.join(", ") : getGameSaveErrorMessage(err));
        } else {
          toastr.error(getGameSaveErrorMessage(err));
        }
      } finally {
        setLoading(false);
      }
    },
  });

  const handleTypeChange = (value) => {
    formik.setFieldValue("type", value);
    formik.setFieldValue("target_type", value);
  };

  const handleRestaurantScopeChange = (value) => {
    formik.setFieldValue("restaurant_scope", value);
    if (value === "all") {
      setSelectedRestaurants([]);
    }
  };

  // Auto-update target_visits_required based on restaurant scope
  useEffect(() => {
    if (formik.values.type === "visits") {
      if (formik.values.restaurant_scope === "selected") {
        formik.setFieldValue("target_visits_required", selectedRestaurants.length);
      } else if (formik.values.restaurant_scope === "all") {
        formik.setFieldValue("target_visits_required", totalRestaurantCount);
      }
    }
  }, [selectedRestaurants, formik.values.restaurant_scope, formik.values.type, totalRestaurantCount]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update target_visits_required when total restaurant count is loaded and scope is "all"
  useEffect(() => {
    if (formik.values.type === "visits" && formik.values.restaurant_scope === "all" && totalRestaurantCount > 0) {
      formik.setFieldValue("target_visits_required", totalRestaurantCount);
    }
  }, [totalRestaurantCount]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="page-content rp-page-content">
      <Container fluid>
        <div className="rp-page">
          <div className="rp-page-header">
            <h2 className="rp-page-header__title">
              {mode === "create" ? "Create Game" : "Edit Game"}
            </h2>
            <p className="rp-page-header__subtitle">
              {mode === "create" ? "Add a new game to the platform" : "Update game information"}
            </p>
          </div>

          <Form onSubmit={formik.handleSubmit}>
            <Row>
              <Col xl={6} lg={6} md={12} sm={12}>
                <CustomInput
                  id="name"
                  label="Game Name"
                  name="name"
                  type="text"
                  placeholder="Enter game name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched.name && !!formik.errors.name}
                  errorText={formik.errors.name}
                  className="white-bg"
                />
              </Col>
              <Col xl={6} lg={6} md={12} sm={12}>
                <CustomInput
                  id="slug"
                  label="Slug"
                  name="slug"
                  type="text"
                  placeholder="Enter game slug (e.g., 4-week-dining-streak)"
                  value={formik.values.slug}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched.slug && !!formik.errors.slug}
                  errorText={formik.errors.slug}
                  className="white-bg"
                  disabled={mode === "edit"}
                />
              </Col>
            </Row>

            <Row>
              <Col sm={12}>
                <CustomInput
                  id="description"
                  label="Description"
                  name="description"
                  type="textarea"
                  placeholder="Enter game description"
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched.description && !!formik.errors.description}
                  errorText={formik.errors.description}
                  className="white-bg"
                  multiline
                />
              </Col>
            </Row>

            <Row>
              <Col xl={6} lg={6} md={12} sm={12}>
                <CustomSelector
                  id="type"
                  label="Game Type"
                  name="type"
                  placeholder="Select game type"
                  options={[
                    { value: "streak", label: "Streak" },
                    { value: "visits", label: "Visits" },
                  ]}
                  value={formik.values.type}
                  onChange={handleTypeChange}
                  invalid={formik.touched.type && !!formik.errors.type}
                  errorText={formik.errors.type}
                  className="white-bg"
                  disabled={mode === "edit"}
                />
              </Col>
              <Col xl={6} lg={6} md={12} sm={12}>
                <CustomInput
                  id="reward_points"
                  label="Reward Points"
                  name="reward_points"
                  type="number"
                  placeholder="Enter reward points"
                  value={formik.values.reward_points}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched.reward_points && !!formik.errors.reward_points}
                  errorText={formik.errors.reward_points}
                  className="white-bg"
                />
              </Col>
            </Row>

            {formik.values.type === "streak" && (
              <Row>
                <Col xl={6} lg={6} md={12} sm={12}>
                  <CustomSelector
                    id="target_duration_weeks"
                    label="Target Duration (Weeks)"
                    name="target_duration_weeks"
                    placeholder="Select target duration"
                    options={[
                      { value: 1, label: "1 Week" },
                      { value: 2, label: "2 Weeks" },
                      { value: 4, label: "4 Weeks" },
                      { value: 8, label: "8 Weeks" },
                    ]}
                    value={formik.values.target_duration_weeks}
                    onChange={(value) => formik.setFieldValue("target_duration_weeks", value)}
                    invalid={formik.touched.target_duration_weeks && !!formik.errors.target_duration_weeks}
                    errorText={formik.errors.target_duration_weeks}
                    className="white-bg"
                  />
                </Col>
              </Row>
            )}

            {formik.values.type === "visits" && (
              <Row>
                <Col xl={6} lg={6} md={12} sm={12}>
                  <CustomInput
                    id="target_visits_required"
                    label="Target Visits Required"
                    name="target_visits_required"
                    type="number"
                    placeholder="Enter target visits required"
                    value={formik.values.target_visits_required}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    invalid={formik.touched.target_visits_required && !!formik.errors.target_visits_required}
                    errorText={formik.errors.target_visits_required}
                    disabled={formik.values.type === "visits"}
                    className="white-bg"
                  />
                </Col>
                <Col xl={6} lg={6} md={12} sm={12}>
                  <CustomInput
                    id="target_timeframe_weeks"
                    label="Target Timeframe (Weeks)"
                    name="target_timeframe_weeks"
                    type="number"
                    placeholder="Enter target timeframe in weeks"
                    value={formik.values.target_timeframe_weeks}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    invalid={formik.touched.target_timeframe_weeks && !!formik.errors.target_timeframe_weeks}
                    errorText={formik.errors.target_timeframe_weeks}
                    className="white-bg"
                  />
                </Col>
              </Row>
            )}

            {formik.values.type === "visits" && (
              <>
                <Row>
                  <Col xl={6} lg={6} md={12} sm={12}>
                    <CustomSelector
                      id="restaurant_scope"
                      label="Eligible Restaurants Scope"
                      name="restaurant_scope"
                      placeholder="Select restaurant scope"
                      options={[
                        { value: "all", label: "All Restaurants" },
                        { value: "selected", label: "Selected Restaurants" },
                      ]}
                      value={formik.values.restaurant_scope}
                      onChange={handleRestaurantScopeChange}
                      invalid={formik.touched.restaurant_scope && !!formik.errors.restaurant_scope}
                      errorText={formik.errors.restaurant_scope}
                      className="white-bg"
                    />
                  </Col>
                  {formik.values.restaurant_scope === "selected" ? (
                    <Col xl={6} lg={6} md={12} sm={12}>
                      <CustomMultiSelector
                        id="restaurants"
                        label="Select Restaurants"
                        name="restaurants"
                        placeholder="Search and select restaurants"
                        options={restaurants}
                        value={selectedRestaurants}
                        onChange={setSelectedRestaurants}
                        loading={loadingRestaurants}
                        onLoadMore={handleLoadMoreRestaurants}
                        hasMore={hasMoreRestaurants}
                        className="white-bg"
                      />
                    </Col>
                  ) : (
                    <Col xl={6} lg={6} md={12} sm={12}>
                      <CustomSelector
                        id="is_active"
                        label="Status"
                        name="is_active"
                        placeholder="Select status"
                        options={[
                          { value: true, label: "Active" },
                          { value: false, label: "Inactive" },
                        ]}
                        value={formik.values.is_active}
                        onChange={(value) => formik.setFieldValue("is_active", value)}
                        className="white-bg"
                      />
                    </Col>
                  )}
                </Row>

                {formik.values.restaurant_scope === "selected" && (
                  <Row>
                    <Col xl={6} lg={6} md={12} sm={12}>
                      <CustomSelector
                        id="is_active"
                        label="Status"
                        name="is_active"
                        placeholder="Select status"
                        options={[
                          { value: true, label: "Active" },
                          { value: false, label: "Inactive" },
                        ]}
                        value={formik.values.is_active}
                        onChange={(value) => formik.setFieldValue("is_active", value)}
                        className="white-bg"
                      />
                    </Col>
                  </Row>
                )}
              </>
            )}

            {formik.values.type === "streak" && (
              <Row>
                <Col xl={6} lg={6} md={12} sm={12}>
                  <CustomSelector
                    id="is_active"
                    label="Status"
                    name="is_active"
                    placeholder="Select status"
                    options={[
                      { value: true, label: "Active" },
                      { value: false, label: "Inactive" },
                    ]}
                    value={formik.values.is_active}
                    onChange={(value) => formik.setFieldValue("is_active", value)}
                    className="white-bg"
                  />
                </Col>
              </Row>
            )}

            <div className="mt-2">
              <SecondaryButton
                type="submit"
                variant="orange"
                disabled={loading}
                loading={loading}
                icon={tickIcon}
                iconPosition="right"
              >
                {loading ? "Saving..." : mode === "create" ? "Create Game" : "Update Game"}
              </SecondaryButton>
              <SecondaryButton
                type="button"
                variant="light-grey"
                onClick={() => navigate("/admin/games")}
                icon={cancelIcon}
                iconPosition="right"
                style={{ marginLeft: "10px" }}
              >
                Cancel
              </SecondaryButton>
            </div>
          </Form>
        </div>
      </Container>
    </div>
  );
};

export default AdminGameCreatePage;
